import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
// TODO(DS): replace with @tb/icon when DS icon set is available
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuGroup = DropdownMenuPrimitive.Group;
export const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
export const DropdownMenuSub = DropdownMenuPrimitive.Sub;
export const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

export const DropdownMenuTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.Trigger
    ref={ref}
    className={cn(
      // Styled as a regular underline field (matches Input/Select) — only
      // the menu popup's chrome comes from the dropdown component, not the
      // trigger's, so this field keeps looking like Name/Email/etc.
      "group flex h-11 w-full cursor-pointer items-center justify-between gap-2 rounded-none border-0 border-b-[1.5px] border-solid border-b-[color:var(--tb-border-strong)] bg-transparent py-2.5 text-left text-[17px] text-[var(--fg)] outline-none transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:border-b-tb-oxide focus:border-b-2 focus:border-b-tb-green data-[state=open]:border-b-2 data-[state=open]:border-b-tb-green disabled:cursor-not-allowed disabled:border-dashed disabled:text-[var(--fg-subtle)] aria-[invalid=true]:border-b-[color:var(--tb-error)] focus-visible:shadow-[var(--focus-ring)]",
      className,
    )}
    {...props}
  >
    {/* The value or its placeholder, on one line: a long one ends in an ellipsis, as an input's. */}
    <span className="min-w-0 flex-1 truncate">{children}</span>
    <ChevronDown className="size-3.5 shrink-0 text-[var(--fg-muted)] transition-transform duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] group-data-[state=open]:rotate-180" />
  </DropdownMenuPrimitive.Trigger>
));
DropdownMenuTrigger.displayName = DropdownMenuPrimitive.Trigger.displayName;

/**
 * The DS's own trigger for a menu that is not a form field (Molecules · Dropdown Menu): a 44px
 * pill naming the current choice, with a 15px chevron after it. Open, it inverts to oxide with
 * white text and turns the chevron half way — the catalog's order control (handoff v2 §6.3).
 */
export const DropdownMenuPillTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.Trigger
    ref={ref}
    className={cn(
      "group inline-flex min-h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-[var(--tb-radius-pill)] border border-[var(--tb-border-strong)] bg-transparent px-4 py-2 text-[13px] font-medium leading-[1.3] tracking-normal text-[var(--fg)] outline-none transition-[background-color,color,border-color] duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] focus-visible:shadow-[var(--focus-ring)] data-[state=open]:border-[var(--fg)] data-[state=open]:bg-[var(--fg)] data-[state=open]:text-tb-white",
      className,
    )}
    {...props}
  >
    {children}
    <span className="inline-flex text-[var(--fg-muted)] group-data-[state=open]:text-tb-white">
      <ChevronDown
        aria-hidden="true"
        strokeWidth={2}
        className="size-[15px] flex-none transition-transform duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] group-data-[state=open]:rotate-180"
      />
    </span>
  </DropdownMenuPrimitive.Trigger>
));
DropdownMenuPillTrigger.displayName = "DropdownMenuPillTrigger";

export const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 8, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 min-w-[var(--radix-dropdown-menu-trigger-width)] rounded-[var(--tb-radius-sm)] border border-[color:var(--tb-border)] bg-tb-white p-[6px] shadow-[var(--tb-shadow-lg)] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-top-1 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

export const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      "flex h-10 cursor-pointer items-center gap-[11px] rounded-[var(--tb-radius-xs)] px-3 text-[14px] text-[var(--fg)] outline-none transition-colors duration-[var(--tb-duration-1)] ease-[var(--tb-ease-linen)] hover:bg-[var(--tb-porcelain-tint)] focus:bg-[var(--tb-porcelain-tint)] data-[disabled]:pointer-events-none data-[disabled]:opacity-50 focus-visible:shadow-[var(--focus-ring)]",
      className,
    )}
    {...props}
  >
    <span className="flex-1">{children}</span>
    <DropdownMenuPrimitive.ItemIndicator className="ml-auto text-tb-green">
      <Check className="size-4" />
    </DropdownMenuPrimitive.ItemIndicator>
  </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

/**
 * A plain action of a menu (the Relatório's ⋯, handoff §6.4): 13px, 9px by 10px, the porcelain
 * tint on hover and focus. A disabled item stays in place, readable and out of the pointer's way.
 */
export const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      "flex cursor-pointer select-none items-center gap-2.5 rounded-[var(--tb-radius-xs)] px-2.5 py-[9px] text-[13px] text-[var(--fg)] outline-none transition-colors duration-[var(--tb-duration-1)] ease-[var(--tb-ease-linen)] hover:bg-[var(--tb-porcelain-tint)] focus:bg-[var(--tb-porcelain-tint)] data-[disabled]:cursor-default data-[disabled]:text-[var(--fg-subtle)] data-[disabled]:hover:bg-transparent focus-visible:shadow-[var(--focus-ring)]",
      className,
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

export const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      "px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--fg-subtle)]",
      className,
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

export const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn("-mx-1.5 my-1.5 h-px bg-[var(--tb-hairline)]", className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
